import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import { userApi } from "@/api/user";
import { USER_QUERY_KEY } from "@/modules/shared/hooks/use-user";
import { useShareAnalyticsEvent } from "@/modules/analytics/hooks/use-share-analytics-event";
import { EAnalyticsEvent } from "@/modules/analytics/consts/analytics-event";
import { logMsg, logEx, crumb } from "@/config/sentry-logger";

import type { AxiosError, AxiosResponse } from "axios";
import type { TUpdateUserRequestData } from "@/modules/shared/types/user";
import type { RootState } from "@/store";

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const initData = useSelector((state: RootState) => state.telegram.initData);
  const { shareAnalyticsEvent } = useShareAnalyticsEvent();

  const {
    mutateAsync: updateUser,
    isPending: isUpdatingUser,
    ...queryParams
  } = useMutation<AxiosResponse, unknown, TUpdateUserRequestData>({
    mutationKey: ["update-user", initData],
    mutationFn: async (requestData) => {
      if (!initData) {
        logMsg("updateUser rejected: no-init-data", "warning", {
          hasInitData: false,
        });
        throw new Error("no-init-data");
      }

      const payload: TUpdateUserRequestData = {
        ...requestData,
        ...(requestData.wallets ? { wallets: [...requestData.wallets] } : {}),
      };

      crumb("updateUser mutation start", {
        hasInitData: true,
        payloadKeys: Object.keys(payload),
        hasWallets: !!requestData.wallets,
        walletsCount: Array.isArray(requestData.wallets)
          ? requestData.wallets.length
          : 0,
      });

      return userApi.updateUser(initData, payload);
    },

    onSuccess: async (_response, requestData) => {
      crumb("updateUser success");
      logMsg("updateUser success", "info", {
        hasWallets: !!requestData.wallets,
        walletsCount: Array.isArray(requestData.wallets)
          ? requestData.wallets.length
          : 0,
      });

      if (requestData.wallets) {
        try {
          await shareAnalyticsEvent(EAnalyticsEvent.WALLET_WAS_ADDED);
          logMsg("WALLET_WAS_ADDED analytics sent", "info");
        } catch (e) {
          logMsg("WALLET_WAS_ADDED analytics failed", "warning");
          logEx(e, "shareAnalyticsEvent WALLET_WAS_ADDED");
        }
      }

      queryClient.setQueryData([USER_QUERY_KEY, initData], _response);
    },

    onError: (error, variables) => {
      const ax = error as AxiosError;
      const status = ax?.response?.status;
      const extra = {
        status,
        code: (ax as any)?.code,
        method: ax?.config?.method,
        url: ax?.config?.url,
        hasInitData: !!initData,
        hasWallets: !!variables?.wallets,
        walletsCount: Array.isArray(variables?.wallets)
          ? variables!.wallets!.length
          : 0,
      };
      const level = !status ? "error" : status >= 500 ? "error" : "warning";

      logMsg("updateUser error", level, extra);
      logEx(error, "updateUser mutation error", extra);
    },
  });

  return {
    updateUser,
    isUpdatingUser,
    ...queryParams,
  };
};
